/**
 * @documental
 * @export MultiSelectDropdown
 */
import { ForwardedRef, ReactNode, forwardRef } from "react";
import { SelectInstance } from "react-select";

import { AccessibilityProps } from "../../AccessibilityProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { InputChangeHandler } from "../InputChangeHandler";
import { RefComponent } from "../RefComponent";
import { Field } from "./Field";
import { Group } from "./Group";
import { Input } from "./Input";
import { Option } from "./Option";
import { OptionItem } from "./OptionItem";
import { Placeholder } from "./Placeholder";

/**
 * The `MultiSelectDropdown` component properties.
 */
export interface MultiSelectDropdownProps
  extends InputBaseProps<ReadonlyArray<string | number> | null>,
    Pick<AccessibilityProps, "aria-label"> {
  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Indicates whether the dropdown is able to be searched. If not specified defaults to `false`.
   */
  readonly searchable?: boolean;

  /**
   * Called when the search value changes.
   */
  readonly onSearchChange?: InputChangeHandler<string | null>;
}

/**
 * The `MultiSelectDropdown` component sub components.
 */
export interface MultiSelectDropdownSubComponents {
  /**
   * A React Hook Form compatible `MultiSelectDropdown` component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * Represents a single option which can be placed within the `MultiSelectDropdown` component.
   */
  readonly Option: typeof Option;

  /**
   * A placeholder component for the `MultiSelectDropdown` which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;

  /**
   * Represents a group of options.
   */
  readonly Group: typeof Group;
}

/**
 * A component which allows for the selection of multiple options from a dropdown list.
 */
export const MultiSelectDropdown = withSubComponents<
  RefComponent<SelectInstance<OptionItem | null, boolean>, MultiSelectDropdownProps>,
  MultiSelectDropdownSubComponents
>(
  {
    Option,
    Field,
    Placeholder,
    Group,
  },
  forwardRef(function MultiSelectDropdown(props: MultiSelectDropdownProps, ref: ForwardedRef<SelectInstance<OptionItem | null, boolean>>) {
    const { children } = props;

    return (
      <Input {...props} ref={ref}>
        {children}
      </Input>
    );
  }),
);
